import Highcharts from 'highcharts'
import { useEffect, useState } from 'react'
import { useTheme } from '@mui/styles'
import { FIGMA_ACCENT_COLORS } from '../../../style/colors'
import { Activity } from '../../queries/useGetActivitiesQuery'
import { useIsDarkMode } from '../../hooks/useIsDarkMode'
import { toDisplayValue } from '../../utils/displayValue'
import {
    ActivityDateRange,
    ActivityDateType,
    PLANNED_BAR_ALPHA,
    formatQuarterUTC,
    hexWithAlpha,
    resolveActivityDateRange,
} from '../../utils/resolveActivityTimelineDates'

export const GANTT_CHART_COLORS = [
    FIGMA_ACCENT_COLORS.blue,
    FIGMA_ACCENT_COLORS.green,
    FIGMA_ACCENT_COLORS.purple,
    FIGMA_ACCENT_COLORS.orange,
    FIGMA_ACCENT_COLORS.salmon,
    FIGMA_ACCENT_COLORS.red,
    FIGMA_ACCENT_COLORS.pink,
    FIGMA_ACCENT_COLORS.yellow,
    FIGMA_ACCENT_COLORS.teal,
    FIGMA_ACCENT_COLORS.neon,
    FIGMA_ACCENT_COLORS.lavender,
]

interface GanttChartActivity {
    name: string
    activityFunction: string
    activityCategory: string
    start: number
    end: number
    budget: string
    startDateType: ActivityDateType
    endDateType: ActivityDateType
    isPlanned: boolean
    color?: string
}

const useGanttChartOptions = (activities: Activity[] | null, isInteractive: boolean): Highcharts.Options | null => {
    const [ganttChartOptions, setGanttChartOptions] = useState<Highcharts.Options>({})

    const theme = useTheme()
    const isDarkMode = useIsDarkMode()

    useEffect(() => {
        if (!activities) return

        const formatActivityData = (activity: Activity, dateRange: ActivityDateRange): GanttChartActivity => ({
            name: activity.title,
            activityFunction: activity.function,
            activityCategory: activity.category,
            start: dateRange.start,
            end: dateRange.end,
            budget: activity.budget,
            startDateType: dateRange.startDateType,
            endDateType: dateRange.endDateType,
            isPlanned: dateRange.isPlanned,
        })

        const groupActivitiesByFunction = (activities: Activity[]): Record<string, GanttChartActivity[]> => {
            return activities.reduce(
                (acc, activity) => {
                    // Activities fall back to their planned dates; a null range means
                    // neither end resolved to a usable date, so there's no bar to draw.
                    const dateRange = resolveActivityDateRange(activity)
                    if (!dateRange) return acc

                    const formattedActivity = formatActivityData(activity, dateRange)

                    if (!acc[activity.function]) acc[activity.function] = []
                    acc[activity.function].push(formattedActivity)

                    return acc
                },
                {} as Record<string, GanttChartActivity[]>
            )
        }

        const sortActivities = (activities: GanttChartActivity[]): GanttChartActivity[] => {
            return [...activities].sort((a, b) => {
                const funcComparison = a.activityFunction.localeCompare(b.activityFunction)
                if (funcComparison !== 0) return funcComparison
                return a.end - b.end
            })
        }

        const prepareSeriesData = (
            groupedData: Record<string, GanttChartActivity[]>
        ): Highcharts.SeriesOptionsType[] => {
            return Object.entries(groupedData).map(([category, activities], index) => {
                const seriesColor = GANTT_CHART_COLORS[index] || theme.palette.figma.grayscale.tier4

                return {
                    name: category,
                    // Planned-derived bars render as a translucent version of their
                    // series color so forecast timelines read as distinct from actuals.
                    data: sortActivities(activities).map((activity) =>
                        activity.isPlanned
                            ? { ...activity, color: hexWithAlpha(seriesColor, PLANNED_BAR_ALPHA) }
                            : activity
                    ),
                    colorByPoint: false,
                    color: seriesColor,
                    borderColor: theme.palette.figma.grayscale.tier7,
                    borderWidth: 0,
                    borderRadius: 6,
                    enableMouseTracking: isInteractive,
                    dataLabels: {
                        color: theme.palette.figma.accentColors.white,
                        style: {
                            textOutline: isDarkMode ? 'none' : `1px solid ${theme.palette.figma.grayscale.textHier1}`,
                            fontWeight: '700',
                            fontSize: '12px',
                            fontFamily: 'acumin-pro',
                        },
                    },
                    type: 'gantt',
                }
            })
        }

        const groupedData = groupActivitiesByFunction(activities)
        const seriesArray = prepareSeriesData(groupedData)

        const options: Highcharts.Options = {
            chart: {
                backgroundColor: 'transparent',
                reflow: true,
            },
            credits: {
                enabled: false,
            },
            tooltip: {
                enabled: isInteractive,
                backgroundColor: theme.palette.figma.grayscale.contentWrapper,
                padding: 12,
                borderRadius: 8,
                borderWidth: 0,
                shadow: false,
                useHTML: true,
                formatter: function () {
                    const point = this.point as unknown as GanttChartActivity
                    const start = new Date(this.x)
                    const end = this.point.x2 ? new Date(this.point.x2) : null

                    const plannedMarker = (dateType: ActivityDateType) => (dateType === 'planned' ? ' (planned)' : '')

                    const textColor = theme.palette.figma.grayscale.textHier1
                    const fontFamily = 'acumin-pro, sans-serif'

                    const containerStyle = `font-family: ${fontFamily}; color: ${textColor};`
                    const titleStyle = `font-size: 14px; font-weight: 700; text-align: center; margin-bottom: 6px;`
                    const rowStyle = `display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; font-size: 13px;`
                    const labelStyle = `font-weight: 600; margin-right: 12px;`
                    const hrStyle = `border: 0; border-top: 1px solid ${theme.palette.figma.grayscale.stroke}; margin: 8px 0;`

                    return `
                        <div style="${containerStyle}">
                            <div style="${titleStyle}">${toDisplayValue(point.activityFunction)}</div>
                            <div style="${rowStyle}">
                                <span style="${labelStyle}">Budget:</span>
                                <span>$${point.budget}</span>
                            </div>
                            <div style="${rowStyle}">
                                <span style="${labelStyle}">Category:</span>
                                <span>${toDisplayValue(point.activityCategory)}</span>
                            </div>
                            <hr style="${hrStyle}" />
                            <div style="${rowStyle}">
                                <span style="${labelStyle}">Start:</span>
                                <span>${formatQuarterUTC(start)}${plannedMarker(point.startDateType)}</span>
                            </div>
                            <div style="${rowStyle}">
                                <span style="${labelStyle}">End:</span>
                                <span>${end ? `${formatQuarterUTC(end)}${plannedMarker(point.endDateType)}` : 'N/A'}</span>
                            </div>
                        </div>
                    `
                },
            },
            xAxis: [
                {
                    grid: {
                        cellHeight: 30,
                    },
                    lineColor: theme.palette.figma.grayscale.stroke,
                    tickColor: theme.palette.figma.grayscale.stroke,
                    type: 'datetime',
                    units: [['month', [3, 6]]],
                    labels: {
                        formatter: function () {
                            // @ts-expect-error copypaste old code
                            if (this.target.isFirst) {
                                var quarter = Math.ceil((new Date(this.value).getMonth() + 1) / 3)
                                // @ts-expect-error copypaste old code
                                return Highcharts.dateFormat('Q' + quarter, this.value)
                            }
                            var quarter = Math.ceil((new Date(this.value).getMonth() + 1) / 3) + 1
                            quarter = quarter === 5 ? 1 : quarter
                            // @ts-expect-error copypaste old code
                            return Highcharts.dateFormat('Q' + quarter, this.value)
                        },
                        align: 'center',
                        style: {
                            color: theme.palette.figma.grayscale.textHier1,
                            fontSize: '16px',
                            fontWeight: '400',
                            fontFamily: 'acumin-pro',
                        },
                    },
                    showLastLabel: false,
                },
                {
                    grid: {
                        cellHeight: 24,
                    },
                    lineColor: theme.palette.figma.grayscale.stroke,
                    tickColor: theme.palette.figma.grayscale.stroke,

                    type: 'datetime',
                    labels: {
                        align: 'center',
                        style: {
                            color: theme.palette.figma.grayscale.textHier1,
                            fontSize: '16px',
                            fontWeight: '700',
                            fontFamily: 'acumin-pro',
                        },
                    },
                },
            ],
            yAxis: {
                lineColor: theme.palette.figma.grayscale.stroke,
                tickColor: theme.palette.figma.grayscale.stroke,
                // alternateGridColor: theme.palette.figma.grayscale.tier6,
                gridLineColor: theme.palette.figma.grayscale.stroke,

                labels: {
                    style: { color: theme.palette.figma.grayscale.textHier1 },
                    padding: 4,
                },
            },
            series: seriesArray,
            navigator: {
                enabled: true,
                liveRedraw: true,
                adaptToUpdatedData: true,
                margin: 0,
                series: {
                    // @ts-expect-error weird typing
                    type: 'gantt',
                    pointPlacement: 0.5,
                    pointPadding: 0.25,
                },
                yAxis: {
                    min: 0,
                    max: 11,
                    reversed: true,
                },
            },
            // scrollbar: {
            //     enabled: true
            // },
            plotOptions: {
                gantt: {
                    pointPadding: 0,
                    dataLabels: {
                        enabled: true,
                        formatter: function (this: Highcharts.PointLabelObject): string {
                            const point = this.point as unknown as GanttChartActivity
                            return point.activityCategory === '---' ? '' : point.activityCategory
                        },
                        style: {
                            color: theme.palette.figma.grayscale.textHier1,
                        },
                    },
                    animation: false,
                },
            },
        }

        setGanttChartOptions(options)
    }, [activities])

    return ganttChartOptions
}

export default useGanttChartOptions
